import { ref } from 'vue'
import APIRequest from '@/api'
import { useAuthStore } from '@/stores/login'
import { updateUserInfo } from '@/utils/userInfo'
import { showToast } from 'vant'
import { FeishuModel } from '@/api/Models/FeishuModel'
import i18n from '@/locales'
import { loading } from '@/utils/loading'
import router from '../router'

export const isFeishu = !!window.h5sdk

interface FeishuLoginParams {
  callback?: () => void
  onError?: () => void
}
const env = process.env.VUE_APP_ENV

// 全局幂等状态与回调队列
let feishuLoginStatus: 'idle' | 'in_progress' | 'done' = 'idle'
const feishuLoginWaiters: Array<{ callback: () => void; onError: () => void }> = []

const notifySuccess = () => {
  feishuLoginStatus = 'done'
  const waiters = feishuLoginWaiters.splice(0)
  waiters.forEach(w => {
    try { w.callback && w.callback() } catch { }
  })
}
const notifyError = () => {
  const waiters = feishuLoginWaiters.splice(0)
  waiters.forEach(w => {
    try { w.onError && w.onError() } catch { }
  })
  feishuLoginStatus = 'idle'
}

export const useFeishuLogin = async (data: FeishuLoginParams = {}) => {
  const { callback = () => { }, onError = () => { } } = data;
  // const tenantId = localStorage.getItem('tenantId')
  try {
    const response = await APIRequest.User.feishuAppid();
    const { data, code } = response;

    if (code === 1 && Array.isArray(data)) {
      const prodAppIds = data.map((item) => item.appId);
      const appIdMap = {
        prod: prodAppIds,
        test: prodAppIds
      };

      window.h5sdk.ready(async () => {
        const { appId } = useAuthStore();

        const appIdsToTry =
          appId && appId !== '' && appId !== null
            ? [appId]
            : env === 'prod'
              ? [...appIdMap.prod]
              : [...appIdMap.test];

        for (const appIdToTry of appIdsToTry) {
          try {
            await attemptAuth(appIdToTry, callback, onError);
            return;
          } catch (error) {
            console.error(`Failed to authenticate with appId ${appIdToTry}: ${error}`);
          }
        }
        onError();
      });
    } else {
      console.error('Failed to fetch appId values');
    }
  } catch (error) {
    console.error('Error fetching appId values:', error);
  }

};

// 文件：feishu.ts 中的相关方法
const attemptAuth = async (appId: string, callback: () => void, onError: () => void) => {
  return new Promise<void>(async (resolve, reject) => {
    try {
      // 仅使用新的 requestAccess
      const resByAccess = await requestFeishuAccess(appId)
      const { code: accessCode } = resByAccess
      if (accessCode === 1) {
        // 登录成功，直接设置 token 和用户信息，并执行回调
        await handleAuthSuccess(resByAccess, callback)
        resolve()
        return
      } else {
        reject(new Error(`Authorization failed via requestAccess with appId ${appId}`))
      }
    } catch (e) {
      reject(e)
    }
  })
}

/**
 * 新版飞书授权：window.tt.requestAccess
 */
// 文件内函数：requestFeishuAccess
const requestFeishuAccess = async (appId: string): Promise<{ code: number; data: any }> => {
  return new Promise((resolve, reject) => {
    const tt = (window as any)?.tt
    if (!tt || typeof tt.requestAccess !== 'function') {
      return reject(new Error('tt.requestAccess not available'))
    }
    try {
      // iOS 端优先使用 requestAuthCode，避免 requestAccess 在 iOS 上的 appid 判定问题
      const isIOS = /iP(hone|od|ad)/i.test(navigator.userAgent || '')
      const doLoginWithCode = async (tmpCode: string) => {
        const loginResp = await APIRequest.User.accountLogin({
          grant_type: 'social',
          socialType: 'feishu',
          code: tmpCode,
          appId
        })
        console.log('accountLogin success', loginResp);
        if (!loginResp || loginResp.code !== 1) {
          showToast(loginResp?.msg || '飞书授权失败')

          // 检查当前是否在直播相关页面
          const currentPath = window.location.hash
          const isLivePage = currentPath.includes('/live') || currentPath.includes('id=') && currentPath.includes('tenantId=')

          if (isLivePage) {
            // 如果是直播页面，保持在当前页面或跳转到直播页面
            const urlParams = new URLSearchParams(window.location.hash.split('?')[1])
            const liveId = urlParams.get('id')
            if (liveId) {
              router.push({
                path: '/live',
                query: { id: liveId, type: urlParams.get('type') || '0' }
              })
            } else {
              router.push({ path: '/liveStream' })
            }
          } else {
            // 判断是否是saas，如果是跳转到选租户页面
            if (window.location.hostname.includes('saas')) {
              router.push({
                path: '/login',
              })
            } else {
              router.push({
                path: '/enterpriseLogin',
              })
            }
          }
          localStorage.removeItem('sandsTalkToken')
          const authStore = useAuthStore()
          authStore.setAccessToken('')
          throw new Error('Authorization failed via code login')
        }
        return loginResp
      }

      const tryRequestAuthCode = () => {
        // 使用 requestAuthCode 兜底获取 code
        if (tt && typeof tt.requestAuthCode === 'function') {
          tt.requestAuthCode({
            appId,
            success: async (resAuth: any) => {
              try {
                if (resAuth?.code) {
                  const loginResp = await doLoginWithCode(resAuth.code)
                  resolve(loginResp)
                } else {
                  showToast('飞书授权返回缺少必要字段(code)')
                  localStorage.removeItem('sandsTalkToken')
                  const authStore = useAuthStore()
                  authStore.setAccessToken('')
                  reject(new Error('requestAuthCode success but missing code'))
                }
              } catch (ex) {
                console.error(`requestAuthCode Error: ${JSON.stringify(ex)}`)
                reject(ex)
              }
            },
            fail: (err2: any) => {
              console.log('requestAuthCode fail', JSON.stringify(err2));
              reject(err2)
            }
          })
          return true
        }
        return false
      }

      // iOS 直接走 requestAuthCode
      if (isIOS) {
        const started = tryRequestAuthCode()
        if (started) return
        // 如果 iOS 端没有 requestAuthCode，则继续尝试 requestAccess（极少见）
      }

      // 默认优先使用 requestAccess（Android 正常）
      tt.requestAccess({
        appId: appId,
        scopeList: [],
        success: async (resSuccess: any) => {
          try {
            // console.log('requestAccess success', resSuccess);
            // 仅走 code/authCode 分支，拿到 code 后直登后端
            if (resSuccess?.code || resSuccess?.authCode) {
              const tmpCode = resSuccess.code || resSuccess.authCode
              const loginResp = await doLoginWithCode(tmpCode)
              resolve(loginResp)
            } else {
              showToast('飞书授权返回缺少必要字段')
              localStorage.removeItem('sandsTalkToken')
              const authStore = useAuthStore()
              authStore.setAccessToken('')
              reject(new Error('requestAccess success but missing expected fields'))
            }
          } catch (ex) {
            console.error(`requestAccess Error: ${JSON.stringify(ex)}`)
            reject(ex)
          }
        },
        fail: (error: any) => {
          const { errno, errString } = error;
          if (errno === 2700002) {
            console.log("用户拒绝授权", errString);
          } else {
            console.log("飞书授权失败");
          }
          // 失败后自动降级到 requestAuthCode（解决 iOS invalid parameter/appid 未识别问题）
          const downgraded = tryRequestAuthCode()
          if (!downgraded) {
            reject(error)
          }
        }
      })
    } catch (e) {
      reject(e)
    }
  })
}

const handleAuthSuccess = async (res: any, callback: () => void) => {
  const authStore = useAuthStore()
  const token = res.data.access_token
  // alert(JSON.stringify(res))
  const tenantId = res.data.tenantId
  authStore.setAccessToken(token)
  localStorage.setItem('sandsTalkToken', token)
  localStorage.setItem('sandsTalkTenantId', tenantId);
  // 新增：为公共域名（saas）接口兼容，同时写入 feishuTenantId
  localStorage.setItem('feishuTenantId', tenantId)
  await updateUserInfo()
  // localStorage.setItem('feishuShowQiutLogin', 'false')
  callback()
}

// 文件内函数：configFeishu（改为无操作以兼容可能的外部引用）
export const configFeishu = async (appId: string) => {
  // 鉴权逻辑已移除，此处保留空实现以避免外部引用报错
  return
}

export function processFeishuData(setAppId: Function, feishuJump: Function, setJumpName: Function) {
  const url = window.location.href
  const urlParams = new URLSearchParams(url.split('?')[1])
  const appId = urlParams.get('appId')
  const state = urlParams.get('state')
  if (appId) {
    setAppId(appId)
  }
  const feishuJumpName = state && state.split('_')[1]
  if (feishuJumpName && feishuJumpName !== '') {
    setJumpName(feishuJumpName)
    feishuJump()
  }
}

export function processFeishuJumpData(url: string, setAppId: Function, setJumpName: Function) {
  const urlParams = new URLSearchParams(url.split('?')[1])
  const appId = urlParams.get('appId')
  const state = urlParams.get('state')
  if (appId) {
    setAppId(appId)
  }
  const feishuJumpName = state && state.split('_')[1]
  if (feishuJumpName) {
    setJumpName(feishuJumpName)
  }
}

export const feishuJump = () => {
  const { jumpName } = useAuthStore()
  switch (jumpName) {
    case 'home':
      router.replace('/')
      break
    case 'studyTask':
      router.replace('/task')
      break
    case 'community':
      router.replace('/community')
      break
  }
}

//自定义导航
export const customNaviBar = () => {
  window.tt.getSystemInfo({
    success(res: FeishuModel) {
      console.log(JSON.stringify(res))
    },
    fail(res: string) {
      console.log(`getSystemInfo fail: ${JSON.stringify(res)}`)
    }
  })
}

export const hideNavBarUrl = (url: string) => {
  const character = url.indexOf('?') > -1 ? '&' : '?'
  url = character + decodeURIComponent('lk_meta={page-meta:{showNavBar:"false"}}')
  return url
}

function compareVersion(v1: string, v2: string) {
  if (!v1) {
    return 1
  }
  const v1arr = v1.split('.')
  const v2arr = v2.split('.')
  const len = Math.max(v1.length, v2.length)
  while (v1arr.length < len) {
    v1arr.push('0')
  }
  while (v2arr.length < len) {
    v2arr.push('0')
  }
  for (let i = 0; i < len; i++) {
    const num1 = parseInt(v1arr[i])
    const num2 = parseInt(v2arr[i])
    if (num1 > num2) {
      return 1
    } else if (num1 < num2) {
      return -1
    }
  }
  return 0
}

interface GetImageInfoRes {
  width: number
  height: number
  errMsg: string
  path: string
}
interface SavaImageToAlbumParams {
  url: string
  callback?: () => void
}
export const saveImageToAlbum = (data: SavaImageToAlbumParams) => {
  loading.show()
  const { url, callback = () => { } } = data
  // console.log(url)

  window.tt.getImageInfo({
    src: url,
    success(res: GetImageInfoRes) {
      console.log(res)
      window.tt.saveImageToPhotosAlbum({
        filePath: res.path,
        success(res2: string) {
          loading.hide()
          showToast(i18n.global.t('shop_saved_sucess'))
          console.log(res2)
          callback()
        },
        fail(err: string) {
          loading.hide()
          console.log(err)
          window.tt.showModal({
            title: i18n.global.t('shop_notice'),
            // content: i18n.global.t('shop_save_image_fail')
            content: JSON.stringify(err)
          })
        }
      })
    },
    fail(err: string) {
      loading.hide()
      console.log(err)
      window.tt.showModal({
        title: i18n.global.t('shop_notice'),
        // content: i18n.global.t('shop_access_image_fail')
        content: JSON.stringify(err)
      })
    }
  })
}
